import Nav from './Nav';
import React, { useState, useEffect, useMemo } from 'react';

const TOOL_META = {
  sermon:       { icon: '📖', label: 'Sermon',          category: 'sermon', primaryField: 'passage' },
  announcement: { icon: '📢', label: 'Announcement',    category: 'comms',  primaryField: 'eventName' },
  discussion:   { icon: '💬', label: 'Discussion Guide',category: 'comms',  primaryField: 'sermonTitle' },
  devotional:   { icon: '🙏', label: 'Devotional',      category: 'sermon', primaryField: 'theme' },
  pastoral:     { icon: '💙', label: 'Pastoral Care',   category: 'comms',  primaryField: 'situationType' },
  evangelism:   { icon: '✝️', label: 'Evangelism Guide',category: 'comms',  primaryField: 'context' },
  prayer:       { icon: '🕊️', label: 'Prayer Response', category: 'comms',  primaryField: 'requestCategory' },
  series:       { icon: '📅', label: 'Series Planner',  category: 'sermon', primaryField: 'theme' },
  biblestudy:   { icon: '📚', label: 'Bible Study',     category: 'sermon', primaryField: 'passage' },
  eventpromo:   { icon: '🎉', label: 'Event Promo',     category: 'comms',  primaryField: 'eventName' },
};

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getDateGroup(isoString) {
  const d = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
  const itemDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (itemDay >= today) return 'Today';
  if (itemDay >= yesterday) return 'Yesterday';
  if (itemDay >= weekAgo) return 'This Week';
  return 'Older';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Older'];

function getPrimaryInput(tool, inputJson) {
  try {
    const parsed = JSON.parse(inputJson);
    const meta = TOOL_META[tool];
    const field = meta ? meta.primaryField : null;
    const val = field && parsed[field] ? parsed[field] : Object.values(parsed).find(v => v && typeof v === 'string') || '';
    return val.length > 60 ? val.slice(0, 57) + '...' : val;
  } catch {
    return inputJson.slice(0, 60);
  }
}

function HistoryItem({ item, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const meta = TOOL_META[item.tool] || { icon: '📄', label: item.tool, category: 'sermon' };
  const badgeColor = meta.category === 'sermon'
    ? { bg: '#dbeafe', text: '#1e40af' }
    : { bg: '#dcfce7', text: '#15803d' };

  const handleCopy = () => {
    navigator.clipboard.writeText(item.output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    onDelete(item.id);
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white mb-3 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div className="text-2xl flex-shrink-0 mt-0.5">{meta.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: badgeColor.bg, color: badgeColor.text }}
            >
              {meta.label}
            </span>
            <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
          </div>
          <p className="text-sm text-gray-700 truncate">{getPrimaryInput(item.tool, item.input)}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors"
          >
            {expanded ? 'Hide' : 'View'}
          </button>
          <button
            onClick={handleCopy}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors disabled:opacity-50"
          >
            {deleting ? '...' : 'Delete'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
          <div
            className="rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap max-h-96 overflow-y-auto"
            style={{ backgroundColor: '#F5F5F5' }}
          >
            {item.output}
          </div>
        </div>
      )}
    </div>
  );
}

export default function History({ token, onBack, onAuthExpired, onNavigate, onLogout, userRole, currentPage }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/history', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.status === 401) {
        if (onAuthExpired) onAuthExpired();
        return;
      }
      if (!res.ok) {
        setError(data.error || 'Failed to load history.');
      } else {
        setHistory(data.history || []);
      }
    } catch {
      setError('Error connecting to server.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/history/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setHistory(prev => prev.filter(item => item.id !== id));
      }
    } catch {
      // silently fail
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return history;
    const q = search.toLowerCase();
    return history.filter(item => {
      const meta = TOOL_META[item.tool];
      return (
        (meta && meta.label.toLowerCase().includes(q)) ||
        item.tool.toLowerCase().includes(q) ||
        item.input.toLowerCase().includes(q) ||
        item.output.toLowerCase().includes(q)
      );
    });
  }, [history, search]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const item of filtered) {
      const g = getDateGroup(item.created_at);
      if (!groups[g]) groups[g] = [];
      groups[g].push(item);
    }
    return groups;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Nav onNavigate={onNavigate} onLogout={onLogout} userRole={userRole} currentPage={currentPage} />

      <main className="flex-1 px-6 py-10 max-w-3xl mx-auto w-full">
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by tool or keyword..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white"
          />
        </div>

        {loading && (
          <div className="text-center text-gray-400 py-20 text-sm">Loading history...</div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">{error}</div>
        )}

        {!loading && !error && history.length === 0 && (
          <div className="text-center text-gray-400 py-20">
            <div className="text-5xl mb-4">🕐</div>
            <p className="text-base">No history yet. Generate something to see it here.</p>
          </div>
        )}

        {!loading && !error && history.length > 0 && filtered.length === 0 && (
          <div className="text-center text-gray-400 py-20 text-sm">
            No results match your search.
          </div>
        )}

        {!loading && !error && GROUP_ORDER.map(group => {
          const items = grouped[group];
          if (!items || items.length === 0) return null;
          return (
            <div key={group} className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{group}</h2>
              {items.map(item => (
                <HistoryItem key={item.id} item={item} onDelete={handleDelete} />
              ))}
            </div>
          );
        })}
      </main>
    </div>
  );
}
